import React from 'react'

import { AssetList } from './components/asset-list'
import { ChangeCompany } from './components/change-company'
import { CommandLog } from './components/command-log'
import { Dashboard } from './components/dashboard'
import { Default } from './components/default'
import { EmailConfirmation } from './components/email-confirmation'
import { EntryEdit } from './components/entry-edit'
import { EntryList } from './components/entry-list'
import { ExpenseArchiveGeneral } from './components/expense/archive-general'
import { ExpenseArchiveResults } from './components/expense/archive-results'
import { ExpenseArchiveTypes } from './components/expense/archive-types'
import { ExpenseArchiveVendor } from './components/expense/archive-vendor'
import { ExpenseEdit } from './components/expense/edit'
import { ExpenseRegister } from './components/expense/register'
import { UnpaidExpenses } from './components/expense/unpaid'
import { FinancialHistoryBalance } from './components/financial-history/balance'
import { GeneralExpenseList } from './components/general-expense-list'
import { GoodsList } from './components/goods-list'
import { InitCompanyBilling } from './components/init-company/billing'
import { InitCompanyDate } from './components/init-company/date'
import { InitCompanyGeneral } from './components/init-company/general'
import { InitCompanyPaymentStatus } from './components/init-company/payment-status'
import { LabourCostList } from './components/labour-cost-list'
import { PasswordReset } from './components/password-reset'
import { BalanceReport } from './components/reports/balance'
import { CashFlowReport } from './components/reports/cash-flow'
import { ChartOfAccounts } from './components/reports/chart-of-accounts'
import { IncomeReport } from './components/reports/income'
import { TurnoverReport } from './components/reports/turnover'
import { RevenueArchiveContents } from './components/revenue/archive-contents'
import { RevenueArchiveCustomer } from './components/revenue/archive-customer'
import { RevenueArchiveGeneral } from './components/revenue/archive-general'
import { RevenueArchiveResults } from './components/revenue/archive-results'
import { RevenueEdit } from './components/revenue/edit'
import { RevenuePreview } from './components/revenue/preview'
import { RevenueRegister } from './components/revenue/register'
import { RevenueSearch } from './components/revenue/search'
import { UnpaidRevenues } from './components/revenue/unpaid'
import { SelectCompany } from './components/select-company'
import { Settings } from './components/settings'
import { SignUp } from './components/signup'
import { StockChange } from './components/stock-change'
import { StockList } from './components/stock-list'
import { TaxesSummary } from './components/taxes/summary'
import { TaxesVat } from './components/taxes/vat'
import { Test } from './components/test'
import { Texts } from './components/texts'
import { UserSettingsCompanies } from './components/user-settings/companies'
import { UserSettingsGeneral } from './components/user-settings/general'
import { fromErrorCode, processWarning } from './error-manager'
import {
    getRevenueArchiveContentsProps,
    getRevenueArchiveCustomerProps,
    getRevenueArchiveGeneralProps,
    getRevenueArchiveResultsProps,
} from './props/revenue-archive-props'
import { getCreditRevenueProps, getRevenueEditProps } from './props/revenue-edit-props'
import { getCreditRevenuePreviewProps, getRevenuePreviewProps } from './props/revenue-preview-props'
import { getRevenueRegisterProps } from './props/revenue-register-props'
import { getTaxesVatProps } from './props/taxes-vat-props'
import { getUnpaidRevenuesProps } from './props/unpaid-revenues-props'
import { showBlank } from './show-blank'
import { RootData } from './state/root-data'

export const renderTopLevelComponent = (rootData: RootData) => {
    const { view } = rootData

    if (!view) {
        throw new Error('View is not set')
    }

    switch (view.name) {
        case 'Blank':
            return null

        case 'AssetList':
            return <AssetList {...rootData} />
        case 'BalanceReport':
            return <BalanceReport {...rootData} />
        case 'CashFlowReport':
            return <CashFlowReport {...rootData} />
        case 'ChartOfAccounts':
            return <ChartOfAccounts {...rootData} />
        case 'ChangeCompany':
            return <ChangeCompany {...rootData} />
        case 'CommandLog':
            return <CommandLog {...rootData} />
        case 'CreditRevenue':
            return <RevenueEdit {...getCreditRevenueProps(rootData)} />
        case 'CreditRevenuePreview':
            return <RevenuePreview {...getCreditRevenuePreviewProps(rootData)} />
        case 'Dashboard':
            return <Dashboard {...rootData} />
        case 'Default':
            return <Default />
        case 'EmailConfirmation':
            return <EmailConfirmation {...rootData} />
        case 'EntryEdit':
            return <EntryEdit {...rootData} />
        case 'EntryList':
            return <EntryList {...rootData} />
        case 'ExpenseArchiveGeneral':
            return <ExpenseArchiveGeneral {...rootData} />
        case 'ExpenseArchiveResults':
            return <ExpenseArchiveResults {...rootData} />
        case 'ExpenseArchiveTypes':
            return <ExpenseArchiveTypes {...rootData} />
        case 'ExpenseArchiveVendor':
            return <ExpenseArchiveVendor {...rootData} />
        case 'ExpenseEdit':
            return <ExpenseEdit {...rootData} />
        case 'ExpenseRegister':
            return <ExpenseRegister {...rootData} />
        case 'FinancialHistoryBalance':
            return <FinancialHistoryBalance {...rootData} />
        case 'GeneralExpenseList':
            return <GeneralExpenseList {...rootData} />
        case 'GoodsList':
            return <GoodsList {...rootData} />
        case 'IncomeReport':
            return <IncomeReport {...rootData} />
        case 'InitCompanyBilling':
            return <InitCompanyBilling {...rootData} />
        case 'InitCompanyDate':
            return <InitCompanyDate {...rootData} />
        case 'InitCompanyGeneral':
            return <InitCompanyGeneral {...rootData} />
        case 'InitCompanyPaymentStatus':
            return <InitCompanyPaymentStatus {...rootData} />
        case 'LabourCostList':
            return <LabourCostList {...rootData} />
        case 'PasswordReset':
            return <PasswordReset {...rootData} />
        case 'RevenueArchiveCustomer':
            return <RevenueArchiveCustomer {...getRevenueArchiveCustomerProps(rootData)} />
        case 'RevenueArchiveContents':
            return <RevenueArchiveContents {...getRevenueArchiveContentsProps(rootData)} />
        case 'RevenueArchiveGeneral':
            return <RevenueArchiveGeneral {...getRevenueArchiveGeneralProps(rootData)} />
        case 'RevenueArchiveResults':
            return <RevenueArchiveResults {...getRevenueArchiveResultsProps(rootData)} />
        case 'RevenueEdit':
            return <RevenueEdit {...getRevenueEditProps(rootData)} />
        case 'RevenuePreview':
            return <RevenuePreview {...getRevenuePreviewProps(rootData)} />
        case 'RevenueRegister':
            return <RevenueRegister {...getRevenueRegisterProps(rootData)} />
        case 'RevenueSearch':
            return <RevenueSearch {...rootData} />
        case 'SelectCompany':
            return <SelectCompany {...rootData} />
        case 'Settings':
            return <Settings {...rootData} />
        case 'SignUp':
            return <SignUp {...rootData} />
        case 'StockChange':
            return <StockChange {...rootData} />
        case 'StockList':
            return <StockList {...rootData} />
        case 'TaxesSummary':
            return <TaxesSummary {...rootData} />
        case 'TaxesVat':
            return <TaxesVat {...getTaxesVatProps(rootData)} />
        case 'Test':
            return <Test {...rootData} />
        case 'Texts':
            return <Texts />
        case 'TurnoverReport':
            return <TurnoverReport {...rootData} />
        case 'UnpaidExpenses':
            return <UnpaidExpenses {...rootData} />
        case 'UnpaidRevenues':
            return <UnpaidRevenues {...getUnpaidRevenuesProps(rootData)} />
        case 'UserSettingsCompanies':
            return <UserSettingsCompanies {...rootData} />
        case 'UserSettingsGeneral':
            return <UserSettingsGeneral {...rootData} />
        default:
            processWarning(
                fromErrorCode('invalid-component', { name: (view as { name: string }).name }),
            )
            showBlank(rootData)
            return null
    }
}
